import { useState, useEffect } from 'react'
import { fmtShortDateTime } from '../../lib/datetime'
import { Activity, Waves, Thermometer, Droplet, Wind, X, Settings, List, Grid, Battery, Navigation, Smartphone, Wifi, Signal, Globe } from 'lucide-react'
import { useApp } from '../../store/AppContext'
import SensorSettings, { SensorConfig } from '../Settings/SensorSettings'
import Sparkline from '../Charts/Sparkline'
import RadarChart from '../Charts/RadarChart'
import BoatOrientation from '../Charts/BoatOrientation'

// Per-sensor color scales for sparklines and value indicators
function getSensorColor(key: string, value: number): string {
    switch (key) {
        // pH: classic indicator scale  red → orange → yellow → green → cyan → blue → violet
        case 'ph': {
            const ph = Math.max(0, Math.min(14, value))
            // Map pH 0-14 → hue: 0 (red) → 60 (yellow) → 120 (green) → 180 (cyan) → 270 (violet)
            const hue = ph <= 7
                ? (ph / 7) * 120           // 0=red(0°) → 7=green(120°)
                : 120 + ((ph - 7) / 7) * 150  // 7=green(120°) → 14=violet(270°)
            return `hsl(${hue}, 80%, 55%)`
        }

        // Temperature: blue (cold) → green (comfortable) → red (hot)
        case 'temp': case 'wt': case 't0': case 't1': case 't2': case 't3': case 't4': case 't5': {
            const t = Math.max(0, Math.min(35, value))
            // 0°C=blue(210°) → 18°C=green(120°) → 35°C=red(0°)
            const hue = 210 - (t / 35) * 210
            return `hsl(${hue}, 75%, 55%)`
        }

        // WiFi/Cell signal: red (weak) → yellow → green (strong)
        case 'wifiDbm': case 'cellDbm': {
            // -100=red → -70=yellow → -40=green
            const clamped = Math.max(-100, Math.min(-30, value))
            const norm = (clamped + 100) / 70  // 0..1
            const hue = norm * 120  // 0=red → 120=green
            return `hsl(${hue}, 80%, 50%)`
        }

        // Battery percentage: red → yellow → green
        case 'bat': case 'soc': {
            const pct = Math.max(0, Math.min(100, value))
            const hue = (pct / 100) * 120  // 0=red → 120=green
            return `hsl(${hue}, 80%, 50%)`
        }

        // System battery voltage (phone LiPo 3.0V–4.2V)
        case 'vbat': {
            const norm = Math.max(0, Math.min(1, (value - 3.0) / 1.2))
            const hue = norm * 120  // 0=red(3.0V) → 120=green(4.2V)
            return `hsl(${hue}, 80%, 50%)`
        }

        // Turbidity: green (clear) → yellow → red (murky)
        case 'turbidity': {
            const norm = Math.max(0, Math.min(1, value / 100))
            const hue = 120 - norm * 120  // 120=green(0 NTU) → 0=red(100 NTU)
            return `hsl(${hue}, 75%, 50%)`
        }

        // Wind: green (calm) → yellow → red (strong)
        case 'wind': case 'windKmh': {
            const norm = Math.max(0, Math.min(1, value / 30))
            const hue = 120 - norm * 120
            return `hsl(${hue}, 75%, 55%)`
        }

        // Default: blue
        default:
            return '#60a5fa'
    }
}

function getTrendArrow(values: number[]): { arrow: string, direction: number } {
    if (values.length < 3) return { arrow: '→', direction: 0 }
    const recent = values[values.length - 1]
    const prev = values.slice(-6, -1)
    const avg = prev.reduce((s, v) => s + v, 0) / prev.length
    const diff = recent - avg
    const threshold = Math.abs(avg) * 0.02 || 0.1 // 2% or 0.1 absolute
    if (diff > threshold) return { arrow: '↑', direction: 1 }
    if (diff < -threshold) return { arrow: '↓', direction: -1 }
    return { arrow: '→', direction: 0 }
}

export default function Dashboard() {
    const { liveData, selectedBoats, dashboardOpen, setDashboardOpen, playbackState, triggerRefresh, t, language, setLanguage, liveDataHistory, allBoatsLiveData, switchToLive, toggleBoatSelection } = useApp()
    const [showSettings, setShowSettings] = useState(false)
    const [showDetailed, setShowDetailed] = useState(false)
    const [sensorConfigs, setSensorConfigs] = useState<SensorConfig[]>([])
    // Auto-hide sensors that have nothing to show. A phone-only boat carries no pH/oxygen probe, so a
    // dozen cards sat there reading "—" and buried the three numbers that WERE live (field report
    // 2026-08-03). Default ON; the header chip reveals them again and remembers the choice.
    const [hideEmpty, setHideEmpty] = useState(() => {
        try { return localStorage.getItem('nida.hideEmptySensors') !== '0' } catch { return true }
    })
    function toggleHideEmpty() {
        setHideEmpty(v => {
            const next = !v
            try { localStorage.setItem('nida.hideEmptySensors', next ? '1' : '0') } catch { /* ignore */ }
            return next
        })
    }

    useEffect(() => {
        const saved = localStorage.getItem('sensor_configs')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                if (Array.isArray(parsed)) {
                    setSensorConfigs(parsed)
                }
            } catch (e) {
                console.error('Failed to load sensor configs', e)
            }
        }
    }, [])

    // Determine data source: Playback or Live
    const firstBoat = selectedBoats[0]
    let data: any = null
    let isPlayback = false

    if (playbackState.frames.length > 0 && playbackState.currentIndex < playbackState.frames.length) {
        data = playbackState.frames[playbackState.currentIndex]
        isPlayback = true
    } else if (firstBoat) {
        data = liveData.get(firstBoat)
    }

    const parsed = data?.parsed

    // Compute which boats are currently live (< 2 min old)
    const now = Date.now()
    const LIVE_THRESHOLD = 120_000
    const liveBoatIds: string[] = []
    allBoatsLiveData.forEach((bd, boatId) => {
        let ts = bd.timestamp
        if (!ts && bd.parsed?.time && typeof bd.parsed.time === 'string') {
            const d = new Date(bd.parsed.time)
            if (!isNaN(d.getTime())) ts = d.getTime()
        }
        if (ts && (now - ts) < LIVE_THRESHOLD) {
            liveBoatIds.push(boatId)
        }
    })

    // Is the current selected boat live?
    const currentBoatIsLive = firstBoat ? liveBoatIds.includes(firstBoat) : false
    // Other live boats (not the currently selected one)
    const otherLiveBoats = liveBoatIds.filter(id => id !== firstBoat)

    // Reusable live-boats banner
    const liveBanner = (otherLiveBoats.length > 0 || (!firstBoat && liveBoatIds.length > 0)) ? (
        <div className="mx-4 my-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-xs text-green-400 mb-1.5">
                <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="font-semibold uppercase tracking-wider">
                    {!firstBoat || !currentBoatIsLive
                        ? `${liveBoatIds.length} ${liveBoatIds.length === 1 ? 'boat' : 'boats'} live now`
                        : `${otherLiveBoats.length} other ${otherLiveBoats.length === 1 ? 'boat' : 'boats'} also live`}
                </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {(!firstBoat ? liveBoatIds : otherLiveBoats).map(boatId => (
                    <button
                        key={boatId}
                        onClick={() => { switchToLive(boatId); if (!selectedBoats.includes(boatId)) toggleBoatSelection(boatId); setDashboardOpen(true) }}
                        className="px-2 py-0.5 text-xs font-mono rounded bg-green-500/20 text-green-300 hover:bg-green-500/40 hover:text-green-100 transition-colors border border-green-500/20"
                    >
                        {boatId}
                    </button>
                ))}
            </div>
        </div>
    ) : null

    if (!dashboardOpen || !parsed) {
        return dashboardOpen ? (
            <div className="absolute top-4 right-4 z-10 w-72">{liveBanner}</div>
        ) : (
            <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
                <button
                    onClick={() => setDashboardOpen(true)}
                    className="glass px-3 py-2 rounded-lg hover:bg-white/90 transition-all"
                >
                    <Activity className="w-5 h-5" />
                </button>
                {liveBanner && <div className="w-64">{liveBanner}</div>}
            </div>
        )
    }

    // Debug log
    console.log('Dashboard render:', { data, parsed })

    function formatValue(val: any, digits: number = 2) {
        if (typeof val === 'number') return val.toFixed(digits)
        if (typeof val === 'string') {
            const num = parseFloat(val)
            if (!isNaN(num)) return num.toFixed(digits)
        }
        return undefined
    }

    // Helper to find value with fallback keys
    function getValue(keys: string[]): any {
        for (const key of keys) {
            if (parsed[key] !== undefined && parsed[key] !== null) {
                return parsed[key]
            }
        }
        return undefined
    }

    function getSignalColor(dbm: number) {
        if (dbm >= -50) return 'text-green-400'
        if (dbm >= -70) return 'text-yellow-400'
        return 'text-red-400'
    }

    const allSensors = [
        // Water — with health ranges for radar chart & color coding
        { key: 'temp', keys: ['temp', 't0'], icon: Thermometer, label: t('sensors.temp'), unit: '°C', color: 'text-orange-400', healthMin: 0, healthMax: 35 },
        // sensor first, forecast separate — internet data must never wear a sensor's label
        { key: 'wt', keys: ['t1'], icon: Thermometer, label: t('sensors.wt'), unit: '°C', color: 'text-orange-400', healthMin: 0, healthMax: 30 },
        { key: 'wtFc', keys: ['wt_fc', 'wt'], icon: Thermometer, label: t('sensors.wtFc'), unit: '°C', color: 'text-sky-300', healthMin: 0, healthMax: 30 },
        // pH: the server's post-hoc calibration from the stored probe voltages first (ph_cal / ph2_cal), then
        // the box's own number; probe 2 when probe 1 sends nothing (an uncalibrated probe 1 prints NaN)
        { key: 'ph', keys: ['ph_cal', 'ph', 'ph2_cal', 'ph2'], icon: Waves, label: t('sensors.ph'), unit: '', color: 'text-blue-400', healthMin: 0, healthMax: 14, radar: true },
        { key: 'o2', keys: ['o2'], icon: Droplet, label: t('sensors.o2'), unit: 'mg/L', color: 'text-cyan-400', healthMin: 0, healthMax: 14, radar: true },
        { key: 'nh4', keys: ['nh4'], icon: Activity, label: t('sensors.nh4'), unit: 'mg/L', color: 'text-green-400', healthMin: 0, healthMax: 5 },
        { key: 'turbidity', keys: ['turbidity'], icon: Waves, label: t('sensors.turbidity'), unit: 'NTU', color: 'text-yellow-400', healthMin: 0, healthMax: 100, radar: true },
        { key: 'clarity', keys: ['clarity', 'turbidity', 'turb'], icon: Droplet, label: t('sensors.clarity'), unit: '%', color: 'text-teal-400', healthMin: 0, healthMax: 100 },

        // Wind
        { key: 'wind', keys: ['wind'], icon: Wind, label: t('sensors.wind'), unit: 'm/s', color: 'text-sky-400', healthMin: 0, healthMax: 30 },
        { key: 'windKmh', keys: ['wind_kmh', 'windKmh'], icon: Wind, label: t('sensors.windKmh'), unit: 'km/h', color: 'text-sky-300', healthMin: 0, healthMax: 100 },

        // Temp Array
        { key: 't1', keys: ['t1'], icon: Thermometer, label: t('sensors.t1'), unit: '°C', color: 'text-orange-300', healthMin: 0, healthMax: 35 },
        { key: 't2', keys: ['t2'], icon: Thermometer, label: t('sensors.t2'), unit: '°C', color: 'text-orange-300', healthMin: 0, healthMax: 35 },
        { key: 't3', keys: ['t3'], icon: Thermometer, label: t('sensors.t3'), unit: '°C', color: 'text-orange-300', healthMin: 0, healthMax: 35 },
        { key: 't4', keys: ['t4'], icon: Thermometer, label: t('sensors.t4'), unit: '°C', color: 'text-orange-300', healthMin: 0, healthMax: 35 },
        { key: 't5', keys: ['t5'], icon: Thermometer, label: t('sensors.t5'), unit: '°C', color: 'text-orange-300', healthMin: 0, healthMax: 35 },

        // System
        { key: 'bat', keys: ['phoneBat', 'bat'], icon: Smartphone, label: t('sensors.bat'), unit: '%', color: 'text-blue-400', healthMin: 0, healthMax: 100 },
        { key: 'vbat', keys: ['vbat'], icon: Battery, label: t('sensors.vbat'), unit: 'V', color: 'text-red-400', healthMin: 3.0, healthMax: 4.2 },
        { key: 'soc', keys: ['soc'], icon: Battery, label: t('sensors.soc'), unit: '%', color: 'text-green-500', healthMin: 0, healthMax: 100 },
        {
            key: 'wifiDbm',
            keys: ['wifiDbm', 'wifi_dbm', 'wifiLvl'],
            icon: Wifi,
            label: t('sensors.wifiDbm'),
            unit: 'dBm',
            color: 'text-purple-400',
            healthMin: -100, healthMax: -30,
            getColor: (val: number) => getSignalColor(val)
        },
        {
            key: 'cellDbm',
            keys: ['cellDbm', 'cell_dbm', 'cellLvl'],
            icon: Signal,
            label: t('sensors.cellDbm'),
            unit: 'dBm',
            color: 'text-purple-400',
            healthMin: -120, healthMax: -40,
            getColor: (val: number) => getSignalColor(val)
        },

        // Environment
        { key: 'phpa', keys: ['phpa'], icon: Wind, label: t('sensors.phpa'), unit: 'hPa', color: 'text-purple-400', healthMin: 950, healthMax: 1050 },
    ]

    // Extract history values for a sensor key from the history buffer
    const boatId = data?.boatId || firstBoat
    const history = liveDataHistory.get(boatId) || []

    function getHistoryValues(keys: string[]): number[] {
        return history
            .map(frame => {
                if (!frame.parsed) return undefined
                for (const k of keys) {
                    if (frame.parsed[k] !== undefined && frame.parsed[k] !== null) {
                        const n = Number(frame.parsed[k])
                        return isNaN(n) ? undefined : n
                    }
                }
                return undefined
            })
            .filter((v): v is number => v !== undefined)
    }

    // Build radar chart axes from sensors marked with radar:true that have data
    const radarAxes = allSensors
        .filter((s: any) => s.radar)
        .map(s => {
            const val = getValue(s.keys)
            return val !== undefined && val !== null
                ? { label: s.label, value: Number(val), min: s.healthMin ?? 0, max: s.healthMax ?? 100, unit: s.unit }
                : null
        })
        .filter((a): a is NonNullable<typeof a> => a !== null)

    // A sensor is EMPTY when it has neither a current reading nor anything in the history buffer —
    // i.e. this boat simply does not carry that probe.
    const hasAnyData = (s: typeof allSensors[number]) => {
        const v = getValue(s.keys)
        if (v !== undefined && v !== null && !(typeof v === 'number' && isNaN(v))) return true
        return getHistoryValues(s.keys).length > 0
    }
    const emptyCount = allSensors.filter(s => {
        const config = sensorConfigs.find(c => c.key === s.key)
        if (config && !config.visible) return false          // already hidden on purpose — don't double-count
        if (/^t[1-5]$/.test(s.key) && !(config && config.visible)) return false
        return !hasAnyData(s)
    }).length

    // Filter sensors based on visibility settings
    const visibleSensors = allSensors.filter(s => {
        const config = sensorConfigs.find(c => c.key === s.key)
        // "soc"/"vbat" are the WATER-SENSOR BOARD's battery — meaningless on a phone-only boat, where
        // they arrive as 0 and read as a confusing "Charge 0.00%". Show them only when the board is
        // actually reporting a real charge (>0). The phone's own battery is the separate "Phone Battery".
        if (s.key === 'soc' || s.key === 'vbat') {
            const v = getValue(s.keys)
            if (!(typeof v === 'number' && v > 0)) return false
        }
        // t1 already HEADLINES the "Water Temp" card (wt reads ['wt','t1']) — its own small card is
        // a confusing duplicate, so tN probe cards are opt-in via SensorSettings, never default-on.
        if (/^t[1-5]$/.test(s.key) && !(config && config.visible)) return false
        // Nothing to show and the user asked for a tidy panel → drop it.
        if (hideEmpty && !hasAnyData(s)) return false
        // Default to visible if not found in config (or if config is empty/loading)
        return config ? config.visible : true
    })

    const handleRefresh = () => {
        triggerRefresh()
    }

    return (
        <div className="absolute top-4 right-4 z-10 w-96 glass-dark rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between flex-none">
                <div className="flex items-center gap-2">
                    <Activity className={`w-5 h-5 ${isPlayback ? 'text-yellow-400' : 'text-primary-400'}`} />
                    <h2 className="font-semibold text-white">
                        {showDetailed ? t('dashboard.detailed') : (isPlayback ? t('dashboard.playback') : t('dashboard.title'))}
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    {(emptyCount > 0 || !hideEmpty) && (
                        <button
                            onClick={toggleHideEmpty}
                            className="px-2 py-1 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors text-[10px] font-semibold whitespace-nowrap"
                            title={hideEmpty
                                ? `${emptyCount} sensor${emptyCount === 1 ? '' : 's'} with no data are hidden — click to show them`
                                : 'Hide sensors that have no data'}>
                            {hideEmpty ? `+${emptyCount} empty` : 'hide empty'}
                        </button>
                    )}
                    <button
                        onClick={() => setLanguage(language === 'en' ? 'lt' : 'en')}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors flex items-center gap-1 text-xs font-bold"
                        title="Switch Language"
                    >
                        <Globe className="w-3 h-3" />
                        {language.toUpperCase()}
                    </button>
                    <button
                        onClick={handleRefresh}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                        title={t('dashboard.refresh')}
                    >
                        <Activity className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setShowDetailed(!showDetailed)}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                        title={t('dashboard.switchView')}
                    >
                        {showDetailed ? <Grid className="w-4 h-4" /> : <List className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={() => setShowSettings(true)}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                        title={t('dashboard.settings')}
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setDashboardOpen(false)}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                        title={t('dashboard.close')}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <SensorSettings
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                onSave={setSensorConfigs}
            />

            {/* Live boats notification banner — shown when current boat is not live */}
            {!currentBoatIsLive && liveBanner}
            {currentBoatIsLive && otherLiveBoats.length > 0 && liveBanner}

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* Boat Info */}
                <div className="p-4 border-b border-white/10">
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="text-sm text-white/60 mb-1">{t('dashboard.boatId')}</div>
                            <div className="text-lg font-semibold text-white">{data.boatId || firstBoat}</div>
                        </div>
                        {isPlayback && (
                            <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs font-medium border border-yellow-500/30">
                                PLAYBACK
                            </span>
                        )}
                    </div>
                    <div className="flex justify-between items-end mt-2">
                        {(() => {
                            // Derive the best available timestamp
                            let effectiveTs = data.timestamp
                            if (!effectiveTs && parsed.time && typeof parsed.time === 'string') {
                                const d = new Date(parsed.time)
                                if (!isNaN(d.getTime())) effectiveTs = d.getTime()
                            }
                            const hasValidTime = effectiveTs > 1000000000000 // after ~2001

                            return (
                                <>
                                    <div>
                                        <div className="text-xs text-white/40">{t('dashboard.lastUpdate')}</div>
                                        <div className="text-sm font-mono text-white/80">
                                            {hasValidTime
                                                ? fmtShortDateTime(effectiveTs)
                                                : '—'}
                                        </div>
                                    </div>
                                    {!isPlayback && (() => {
                                        if (!hasValidTime) {
                                            return (
                                                <div className="text-xs text-white/30 flex items-center gap-1">
                                                    <span className="inline-flex rounded-full h-2 w-2 bg-white/20"></span>
                                                    Offline
                                                </div>
                                            )
                                        }
                                        const dataAge = Date.now() - effectiveTs
                                        const isLive = dataAge < 120_000
                                        const isStale = dataAge < 3_600_000
                                        const agoText = dataAge < 60_000 ? `${Math.round(dataAge/1000)}s ago`
                                                      : dataAge < 3_600_000 ? `${Math.round(dataAge/60_000)}m ago`
                                                      : dataAge < 86_400_000 ? `${Math.round(dataAge/3_600_000)}h ago`
                                                      : `${Math.round(dataAge/86_400_000)}d ago`

                                        if (isLive) {
                                            return (
                                                <div className="text-xs text-green-400 flex items-center gap-1">
                                                    <span className="relative flex h-2 w-2">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                                    </span>
                                                    {t('dashboard.live')}
                                                </div>
                                            )
                                        }
                                        if (isStale) {
                                            return (
                                                <div className="text-xs text-yellow-400 flex items-center gap-1">
                                                    <span className="inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                                                    {agoText}
                                                </div>
                                            )
                                        }
                                        return (
                                            <div className="text-xs text-white/40 flex items-center gap-1">
                                                <span className="inline-flex rounded-full h-2 w-2 bg-white/30"></span>
                                                {agoText}
                                            </div>
                                        )
                                    })()}
                                </>
                            )
                        })()}
                    </div>
                </div>

                {showDetailed ? (
                    <div className="p-4 space-y-4">
                        {/* Detailed List View */}
                        <div className="space-y-1 font-mono text-sm">
                            {Object.entries(parsed).map(([key, val]) => {
                                if (key === 'time' || typeof val === 'object') return null
                                return (
                                    <div key={key} className="flex justify-between py-1 border-b border-white/5">
                                        <span className="text-white/60 uppercase">{key}</span>
                                        <span className="text-white">{String(val)}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Radar Chart — Water Quality Profile */}
                        {radarAxes.length >= 3 && (
                            <div className="p-4 border-b border-white/10">
                                <div className="text-xs text-white/40 text-center mb-1">{t('sensors.waterProfile') || 'Water Quality Profile'}</div>
                                <RadarChart axes={radarAxes} size={180} />
                            </div>
                        )}

                        {/* 3D Boat Orientation */}
                        {parsed.hea !== undefined && (
                            <div className="p-4 border-b border-white/10 flex justify-center">
                                <BoatOrientation
                                    heading={Number(parsed.hea) || 0}
                                    pitch={Number(parsed.pit) || 0}
                                    roll={Number(parsed.roll) || 0}
                                    size={160}
                                />
                            </div>
                        )}

                        {/* Grid View with Sparklines + Trend Arrows */}
                        <div className="p-4 space-y-3">
                            {visibleSensors.map((sensor, idx) => {
                                const Icon = sensor.icon
                                const val = getValue(sensor.keys)
                                // @ts-ignore
                                const color = sensor.getColor ? sensor.getColor(Number(val)) : sensor.color
                                const histValues = getHistoryValues(sensor.keys)
                                const trend = getTrendArrow(histValues)
                                const healthColor = val !== undefined && val !== null
                                    ? getSensorColor(sensor.key, Number(val))
                                    : '#94a3b8'

                                const sparkColor = healthColor

                                return (
                                    <div
                                        key={idx}
                                        className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-all"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`p-2 rounded-lg bg-white/10 ${color} shrink-0`}>
                                                    <Icon className="w-4 h-4" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm text-white/60">{sensor.label}</div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-2xl font-bold text-white">
                                                            {val !== undefined && val !== null
                                                                ? `${formatValue(val)}${sensor.unit}`
                                                                : '—'}
                                                        </span>
                                                        {/* One battery field: phone % headline + the sensor box's own battery when a box is attached */}
                                                        {sensor.key === 'bat' && (() => {
                                                            const bv = getValue(['vbat'])
                                                            const bs = getValue(['soc'])
                                                            if (!(typeof bv === 'number' && bv > 0)) return null
                                                            return <span className="text-xs text-white/50 self-end pb-1">
                                                                · box {Number(bv).toFixed(1)}V{typeof bs === 'number' ? ` (${Math.round(bs)}%)` : ''}
                                                            </span>
                                                        })()}
                                                        {val !== undefined && histValues.length >= 3 && (
                                                            <span
                                                                className="text-lg font-bold"
                                                                style={{ color: trend.direction === 1 ? '#f59e0b' : trend.direction === -1 ? '#3b82f6' : '#94a3b8' }}
                                                                title={trend.direction === 1 ? 'Rising' : trend.direction === -1 ? 'Falling' : 'Stable'}
                                                            >
                                                                {trend.arrow}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Sparkline */}
                                            {histValues.length >= 2 && (
                                                <Sparkline
                                                    values={histValues}
                                                    width={72}
                                                    height={28}
                                                    color={sparkColor}
                                                    fillColor={sparkColor}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {/* GPS & Compass Info */}
                        {(parsed.lat && parsed.lon) || parsed.hea ? (
                            <div className="p-4 border-t border-white/10">
                                <div className="flex items-center gap-2 mb-3">
                                    <Navigation className="w-4 h-4 text-white/40" />
                                    <div className="text-sm text-white/60">{t('dashboard.navigation')}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {parsed.lat && (
                                        <div className="bg-white/5 rounded-lg p-2">
                                            <div className="text-xs text-white/40">{t('dashboard.latitude')}</div>
                                            <div className="text-sm font-mono text-white">{Number(parsed.lat).toFixed(6)}</div>
                                        </div>
                                    )}
                                    {parsed.lon && (
                                        <div className="bg-white/5 rounded-lg p-2">
                                            <div className="text-xs text-white/40">{t('dashboard.longitude')}</div>
                                            <div className="text-sm font-mono text-white">{Number(parsed.lon).toFixed(6)}</div>
                                        </div>
                                    )}
                                    {parsed.hea !== undefined && (
                                        <div className="col-span-2 bg-white/5 rounded-lg p-2 flex items-center justify-between">
                                            <div>
                                                <div className="text-xs text-white/40">{t('dashboard.heading')}</div>
                                                <div className="text-sm font-mono text-white">{Number(parsed.hea).toFixed(1)}°</div>
                                            </div>
                                            <div className="relative w-10 h-10 flex items-center justify-center bg-white/5 rounded-full border border-white/10">
                                                <div className="absolute top-0 text-[8px] text-white/40">N</div>
                                                <Navigation
                                                    className="w-5 h-5 text-primary-400 transition-transform duration-500"
                                                    style={{ transform: `rotate(${parsed.hea}deg)` }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    )
}
